import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { computed } from '@ember/object';
import { readOnly, and, equal, or, gt } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';
import moment from 'moment-timezone';
import { importStateGte } from 'cio/helpers/import-state-gte';
import EnvironmentModel from 'cio/models/environment';
import SegmentModel from 'cio/models/segment';
import UserOtherAccountModel from 'cio/models/user-other-account';
import { IdentifierKey } from 'cio/utils/identifiers';
import { SheetObject } from 'cio/utils/google-sheets';
import {
  ImportMeta,
  ImportPreprocessMeta,
  ImportRules,
  ImportValidateRules,
  IMPORT_PREPROCESS_META_DEFAULTS,
  IMPORT_RULES_DEFAULTS,
  IMPORT_VALIDATE_RULES_DEFAULTS,
  ImportValidateMeta,
  getImportTargetData,
} from 'cio/utils/imports';
import cloneDeep from 'lodash.clonedeep';
import { modelApiAction } from 'cio/utils/model-api-actions';
import { SignResponse } from 'cio/utils/uploads';
import pluralize from 'cio/utils/pluralize';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import { formatNumber } from 'cio/helpers/format-number';

export const MAX_SAMPLES_COUNT = 3;

export enum ImportState {
  Start = 'start',
  Preprocessing = 'preprocessing',
  Preprocessed = 'preprocessed',
  Validating = 'validating',
  Validated = 'validated',
  Importing = 'importing',
  Imported = 'imported',
  Failed = 'failed',
  Canceled = 'canceled',
}

export const UPDATE_TYPES = {
  reset: 'reset',
  reset_preprocessed: 'reset_preprocessed',
  preprocess: 'preprocess',
  validate: 'validate',
  import_rules: 'import_rules',
  validate_rules: 'validate_rules',
  import: 'import',
};

export const LOADING_STATES = [ImportState.Preprocessing, ImportState.Validating, ImportState.Importing];
export const NON_RESETTABLE_STATES = [...LOADING_STATES, ImportState.Imported];

export enum IMPORT_TYPES {
  people = 'people',
  segment = 'segment',
  event = 'event',
  object = 'object',
  relationship = 'relationship',
}

export enum ImportSource {
  UI = 'ui',
  API = 'ext_api',
}

export default class ImportModel extends Model {
  @service store!: DS.Store;

  @attr('string') path!: string;
  @attr('string') filename!: string;
  @attr('camel-case') sheet!: SheetObject;
  @attr('string') name!: string;
  @attr('string') description!: string;
  @attr('string') error!: string;
  @attr('string') segmentName?: string;
  @attr('string') source!: ImportSource;

  @attr('string') type!: IMPORT_TYPES;
  @attr('string') state!: ImportState;
  @attr('string') updateType!: string;
  @attr('number') rowsToImport!: number;
  @attr('number') rowsImported!: number;

  @attr('number') createdAt!: number;
  @attr('number') updatedAt!: number;

  @attr('string') objectTypeId!: string;

  @belongsTo('user-other-account', { async: false, inverse: 'imports' }) user!: UserOtherAccountModel;
  @belongsTo('environment', { async: true, inverse: null }) environment!: EnvironmentModel;
  @belongsTo('segment', { async: true, inverse: null }) segment!: SegmentModel;

  @attr('camel-case')
  preprocessRules?: Record<string, any>;
  @attr('camel-case', { recursive: false, schemaDefaults: IMPORT_PREPROCESS_META_DEFAULTS } as any)
  preprocessMeta!: ImportPreprocessMeta;
  @attr('camel-case', { recursive: false, schemaDefaults: IMPORT_VALIDATE_RULES_DEFAULTS } as any)
  validateRules!: ImportValidateRules;

  changedValidateRule(rule: keyof ImportValidateRules) {
    let changed = this.changedAttributes().validateRules;

    if (changed == null) {
      return false;
    }

    let [oldRules, newRules] = changed;

    return oldRules?.[rule] !== newRules?.[rule];
  }

  @attr('camel-case', { recursive: false } as any)
  validateMeta?: ImportValidateMeta;
  @attr('camel-case', { recursive: false, schemaDefaults: IMPORT_RULES_DEFAULTS } as any)
  importRules!: ImportRules;

  changedImportRule(rule: keyof ImportRules) {
    let changed = this.changedAttributes().importRules;

    if (changed == null) {
      return false;
    }

    let [oldRules, newRules] = changed;

    return oldRules?.[rule] !== newRules?.[rule];
  }

  @attr('camel-case', { recursive: false } as any)
  importMeta?: ImportMeta;

  @equal('source', ImportSource.UI) createdViaUI!: boolean;
  @equal('source', ImportSource.API) createdViaAPI!: boolean;

  @equal('validateMeta.profilesToUpdate', 0) hasNoProfilesToUpdate!: boolean;
  @equal('validateMeta.newObjects', 0) hasNoNewObjects!: boolean;
  @equal('validateMeta.existingObjects', 0) hasNoExistingObjects!: boolean;
  @equal('validateMeta.newRelationships', 0) hasNoNewRelationships!: boolean;
  @equal('validateMeta.existingRelationships', 0) hasNoExistingRelationships!: boolean;
  @or('hasInvalidRows', 'validateMeta.errors.length') hasValidateErrors!: boolean;
  @gt('validateMeta.invalidRows', 0) hasInvalidRows!: boolean;

  @computed('segment', 'validateMeta.{invalidRows,errors,profilesToUpdate}')
  get hasErrors() {
    // Manual segment imports won't update profiles
    if (this.segment != null) {
      return this.hasValidateErrors;
    }

    return this.hasValidateErrors || this.hasNoProfilesToUpdate;
  }

  get importSummaryText() {
    let { validateMeta, validateRules, type, isEventsImport } = this;
    let { target, newTargetCount, existingTargetCount } = getImportTargetData(this);
    target = [IMPORT_TYPES.people, IMPORT_TYPES.event].includes(type) ? 'profile' : target;

    let formattedExistingCount = formatNumber([existingTargetCount]);
    let formattedNewCount = formatNumber([newTargetCount]);

    if (isEventsImport) {
      let rowsImported = (validateMeta?.rows ?? 0) - (validateMeta?.invalidRows ?? 0);
      let summaryText = `This import created ${formatNumberAndPluralize([rowsImported, IMPORT_TYPES.event])} for`;

      if (formattedExistingCount && formattedNewCount) {
        return `${summaryText} ${formattedNewCount} new and
      ${formattedExistingCount} existing profiles.`;
      } else if (formattedExistingCount) {
        return `${summaryText} ${formattedExistingCount} existing ${pluralize(existingTargetCount, target)}.`;
      } else {
        return `${summaryText} ${formattedNewCount} new ${pluralize(newTargetCount, target)}.`;
      }
    }

    let summaryText = 'This import ';

    if (validateRules.identifier !== IdentifierKey.CioId) {
      summaryText += `created ${formattedNewCount} new ${pluralize(newTargetCount, target)} and `;
    }

    summaryText += `updated ${formattedExistingCount} existing ${pluralize(existingTargetCount, target)}`;

    if (validateRules.identifier === IdentifierKey.CioId) {
      return `${summaryText}, including ${formatNumberAndPluralize([
        this.idChanges,
        'id',
      ])} and ${formatNumberAndPluralize([(this as any).email, 'email'])}.`;
    }

    return `${summaryText}.`;
  }

  @computed('validateRules.{onlyExisting,excludeExisting}')
  get importExisting() {
    let onlyExisting = this.validateRules?.onlyExisting || false;
    let excludeExisting = this.validateRules?.excludeExisting || false;

    return onlyExisting || !excludeExisting;
  }

  @computed('validateMeta.{warnings,columnWarnings}')
  get warnings() {
    let meta = this.validateMeta;

    if (meta == null) {
      return [];
    }

    let { warnings, columnWarnings } = meta;

    let columnWarningsArray = Object.keys(columnWarnings || {}).reduce<string[]>((messages, key) => {
      return messages.concat(`${key}: ${columnWarnings[key]}`);
    }, []);

    return [...(warnings ?? []), ...columnWarningsArray];
  }

  @computed('validateMeta.{warnings,warningRows,columnWarnings}')
  get hasWarnings() {
    if (this.validateMeta == null) {
      return false;
    }

    let { warnings, warningRows, columnWarnings } = this.validateMeta;

    return (warnings?.length ?? 0) > 0 || warningRows > 0 || Object.keys(columnWarnings || {}).length > 0;
  }

  @computed('validateRules.{onlyExisting,excludeExisting}')
  get importNew() {
    let onlyExisting = this.validateRules?.onlyExisting || false;
    let excludeExisting = this.validateRules?.excludeExisting || false;

    return !onlyExisting || excludeExisting;
  }

  @computed('importNew', 'validateMeta.existingProfiles')
  get existingProfileCount() {
    let { importExisting, validateMeta } = this;

    if (importExisting && validateMeta?.existingProfiles != null) {
      return validateMeta.existingProfiles;
    }

    return 0;
  }

  @computed('importNew', 'validateMeta.newProfiles')
  get newProfileCount() {
    let { importNew, validateMeta } = this;

    if (importNew && validateMeta?.newProfiles != null) {
      return validateMeta.newProfiles;
    }

    return 0;
  }

  get existingObjectsCount() {
    let { importExisting, validateMeta } = this;

    if (importExisting && validateMeta?.existingObjects != null) {
      return validateMeta.existingObjects;
    }

    return 0;
  }

  get newObjectsCount() {
    let { importNew, validateMeta } = this;

    if (importNew && validateMeta?.newObjects != null) {
      return validateMeta.newObjects;
    }

    return 0;
  }

  get existingRelationshipsCount() {
    let { importExisting, validateMeta } = this;

    if (importExisting && validateMeta?.existingRelationships != null) {
      return validateMeta.existingRelationships;
    }

    return 0;
  }

  get newRelationshipsCount() {
    let { importNew, validateMeta } = this;

    if (importNew && validateMeta?.newRelationships != null) {
      return validateMeta.newRelationships;
    }

    return 0;
  }

  @and('importExisting', 'importNew') importAll!: boolean;

  @computed('validateMeta.{invalidRows,rows}')
  get hasAllInvalidRows() {
    let meta = this.validateMeta;
    let { rows, invalidRows } = meta ?? {};

    return isPresent(rows) && rows !== 0 && invalidRows !== 0 && rows === invalidRows;
  }

  @computed('state', 'type', 'hasAllInvalidRows', 'hasNoProfilesToUpdate', 'existingProfileCount', 'isExpired')
  get canImport() {
    if (this.state === ImportState.Failed || this.hasAllInvalidRows || this.isExpired) {
      return false;
    }

    if (this.type === IMPORT_TYPES.segment) {
      return this.existingProfileCount > 0;
    } else if (this.type === IMPORT_TYPES.object) {
      return !this.hasNoNewObjects || !this.hasNoExistingObjects;
    } else if (this.type === IMPORT_TYPES.relationship) {
      return !this.hasNoNewRelationships || !this.hasNoExistingRelationships;
    } else {
      return !this.hasNoProfilesToUpdate;
    }
  }

  @equal('type', IMPORT_TYPES.event) isEventsImport!: boolean;
  @equal('type', IMPORT_TYPES.people) isPeopleImport!: boolean;
  @equal('type', IMPORT_TYPES.segment) isSegmentImport!: boolean;
  @equal('type', IMPORT_TYPES.object) isObjectsImport!: boolean;
  @equal('type', IMPORT_TYPES.relationship) isRelationshipsImport!: boolean;

  get isProfilesImport() {
    return this.isPeopleImport || this.isObjectsImport || this.isRelationshipsImport;
  }

  @readOnly('validateMeta.emailChanges') emailChanges!: boolean;
  @readOnly('validateMeta.idChanges') idChanges!: boolean;

  sign = modelApiAction<{ name: string; type: string; size: string }, SignResponse & Record<string, any>>({
    path: 'sign',
    type: 'POST',
    collection: true,
  });

  @computed('createdAt')
  get isExpired() {
    if (!this.createdAt) {
      return false;
    }

    let createdAt = moment.unix(this.createdAt);

    return moment().subtract(30, 'days').isAfter(createdAt);
  }

  @computed('sheet', 'isExpired')
  get isOriginalAvailable() {
    if (this.sheet) {
      return true;
    }

    return !this.isExpired;
  }

  // Old imports and segment updated don't have an identifier, so let's provide
  // a computed backup!
  @computed('validateRules.{identifier,matchEmail,noId}')
  get identifier() {
    if (this.validateRules.identifier) {
      return this.validateRules.identifier;
    }

    return this.validateRules.matchEmail || this.validateRules.noId ? IdentifierKey.Email : IdentifierKey.Id;
  }

  get objectType() {
    if (this.objectTypeId == null) {
      return null;
    }

    return this.store.peekRecord('object-type', this.objectTypeId);
  }

  resetState = task({ drop: true }, async () => {
    let { state } = this;

    if (state === ImportState.Failed || (!this.isNew && !NON_RESETTABLE_STATES.includes(state))) {
      this.set('updateType', UPDATE_TYPES.reset);
      await this.save();
    }
  });

  resetStateToPreprocessing = task({ drop: true }, async () => {
    let { state } = this;

    if (importStateGte([state, ImportState.Validating])) {
      this.set('updateType', UPDATE_TYPES.reset_preprocessed);
      await this.save();
    }
  });

  updateImportRules = task({ drop: true }, async (importRules: ImportRules) => {
    this.setProperties({ updateType: UPDATE_TYPES.import_rules, importRules });
    await this.save();
  });

  updateValidateRules = task({ drop: true }, async (validateRules: ImportValidateRules) => {
    this.setProperties({ updateType: UPDATE_TYPES.validate_rules, validateRules });
    await this.save();
  });

  preprocess = task({ drop: true }, async () => {
    // We copy these fragments here, because the following tasks update the import model
    // after the request finishes. Because objects are shared in memory, if we simply
    // destructure the fragments, then they change with the parent model. We need them
    // to not change, since they're used to update those models in separate calls to
    // the back end.
    let validateRules = cloneDeep(this.validateRules ?? {});
    let importRules = cloneDeep(this.importRules ?? {});

    await this.resetState.perform();

    await this.updateImportRules.perform(importRules);
    await this.updateValidateRules.perform(validateRules);

    this.set('updateType', UPDATE_TYPES.preprocess);
    await this.save();
  });

  validate = task({ drop: true }, async () => {
    this.set('updateType', UPDATE_TYPES.validate);
    await this.save();
  });

  setManualSegment = task({ restartable: true }, async (segmentId: string) => {
    let segment = await this.store.findRecord('segment', segmentId);

    if (segment != null) {
      this.setProperties({
        segment,
      });
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    import: ImportModel;
  }
}
